import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TASK_ALERT_QUERY_KEYS } from "@/hooks/useTaskAlerts";
import { taskToastChannelName } from "@/lib/taskRealtimeChannels";

function invalidateTaskAlertKeys(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [TASK_ALERT_QUERY_KEYS.inbox] });
  qc.invalidateQueries({ queryKey: [TASK_ALERT_QUERY_KEYS.sent] });
  qc.invalidateQueries({ queryKey: [TASK_ALERT_QUERY_KEYS.completed] });
  qc.invalidateQueries({ queryKey: [TASK_ALERT_QUERY_KEYS.stats] });
}

interface AssignedTaskRow {
  title?: string | null;
  assigned_by_name?: string | null;
  created_by?: string | null;
}

export function TaskNotificationListener() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // useNavigate's identity changes on every pathname change. Reading it through
  // a ref keeps the subscription keyed on userId/queryClient only — otherwise
  // each navigation tears the realtime channel down and an INSERT landing in
  // the resubscribe gap is never toasted.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!userId) return;

    // Toasts raised by THIS subscription. They are persistent, and the Sonner
    // <Toaster> is mounted outside the protected routes, so without explicit
    // dismissal a previous user's task titles and assigner names would stay on
    // screen after sign-out or a user switch.
    const toastIds = new Set<string | number>();
    let active = true;

    // A new-task toast stays until dismissed: an assignment is easy to miss in
    // an 8-second toast, and the View action takes the user straight to it.
    // closeButton: the global Toaster has none, and an Infinity toast must be
    // dismissable without navigating away.
    const showAssigned = (assigner: string, title: string | null | undefined) => {
      // The NPD branch awaits a profile lookup; if the user changed or the
      // listener unmounted meanwhile, do not resurrect their toast.
      if (!active) return;
      const id = toast.info(`${assigner} assigned you: "${title ?? "a task"}"`, {
        duration: Infinity,
        closeButton: true,
        action: { label: "View", onClick: () => navigateRef.current("/tasks") },
        onDismiss: () => toastIds.delete(id),
      });
      toastIds.add(id);
    };

    // Unique per user AND per mount — see taskRealtimeChannels.ts for why a
    // shared name silently broke both this listener and the /tasks page.
    const channel = supabase
      .channel(taskToastChannelName(userId))
      // General task_assignments
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "task_assignments",
          filter: `assigned_to=eq.${userId}`,
        },
        (payload) => {
          const task = payload.new as AssignedTaskRow;
          queryClient.invalidateQueries({ queryKey: ["my-task-assignments"] });
          queryClient.invalidateQueries({ queryKey: ["my-unified-tasks"] });
          invalidateTaskAlertKeys(queryClient);
          showAssigned(task.assigned_by_name || "An admin", task.title);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "task_assignments",
          filter: `assigned_to=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["my-task-assignments"] });
          queryClient.invalidateQueries({ queryKey: ["my-unified-tasks"] });
          invalidateTaskAlertKeys(queryClient);
        }
      )
      // NPD tasks
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "npd_tasks",
          filter: `assigned_to=eq.${userId}`,
        },
        async (payload) => {
          const task = payload.new as AssignedTaskRow;
          queryClient.invalidateQueries({ queryKey: ["my-npd-tasks"] });
          queryClient.invalidateQueries({ queryKey: ["my-unified-tasks"] });
          invalidateTaskAlertKeys(queryClient);

          let assignerName = "Someone";
          if (task.created_by) {
            const { data: profile } = await supabase
              .from("profiles")
              .select("full_name")
              .eq("user_id", task.created_by)
              .single();
            if (profile?.full_name) assignerName = profile.full_name;
          }

          showAssigned(assignerName, task.title);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "npd_tasks",
          filter: `assigned_to=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["my-npd-tasks"] });
          queryClient.invalidateQueries({ queryKey: ["my-unified-tasks"] });
          invalidateTaskAlertKeys(queryClient);
        }
      )
      .subscribe();

    // Keyed on userId (not the user object) so a token refresh does not churn
    // the subscription, while a sign-out / user switch tears down (dismissing
    // that user's toasts) and resubscribes for the new user.
    return () => {
      active = false;
      toastIds.forEach((id) => toast.dismiss(id));
      toastIds.clear();
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  return null;
}
